import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchProducts, reorderProducts, localReorderProducts } from "../../RTK/productSlice";
import AddProductPopup from "../../main-page/product/product-popup/AddProductPopup";
import DraggableProductCard from "./DraggableProductCard";
import { toast } from "react-toastify";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

import {
  Search,
  PlusCircle,
  Filter,
  Package,
  Boxes,
  Box,
  Grid,
  ShoppingBag,
  Loader2,
  CheckCircle2,
  XCircle,
  Save,
  RotateCcw,
  Lock,
} from "lucide-react";
import {
  StockOptionsModal,
  VoucherManagementModal,
} from "./product-popup/StockManagement";
import { usePermissions } from "../../contexts/PermissionsContext";

const Product = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  // ✅ فحص الصلاحيات
  const canCreateProduct = hasPermission("create_product");
  const canUpdateProduct = hasPermission("update_product");
  const canDeleteProduct = hasPermission("delete_product");
  const { products, loading, error, reordering } = useSelector((state) => state.products);

  const [activeIndex, setActiveIndex] = useState(1);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showAddProductPopup, setShowAddProductPopup] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOption, setFilterOption] = useState("All");
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);

  // Drag and Drop states
  const [activeId, setActiveId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalOrder, setOriginalOrder] = useState([]);

  // Stock management states
  const [showStockOptions, setShowStockOptions] = useState(false);
  const [showVoucherManagement, setShowVoucherManagement] = useState(false);
  const [selectedStockProduct, setSelectedStockProduct] = useState(null);

  // DnD Kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const fetchType = ["all", "quantity", "package", "stock"][activeIndex];
    dispatch(fetchProducts(fetchType));
  }, [dispatch, activeIndex]);

  useEffect(() => {
  const productsKey = ["all", "quantity", "package", "stock"][activeIndex];
  let filtered = products[productsKey] || [];

  if (searchQuery) {
    filtered = filtered.filter((product) =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  if (filterOption !== "All") {
    filtered = filtered.filter(
      (product) => product.status.toLowerCase() === filterOption.toLowerCase()
    );
  }

  // Only update if not dragging/changed
  if (!hasChanges) {
    setFilteredProducts(filtered);
    setOriginalOrder(filtered.map(p => p.id));
  }
}, [products, activeIndex, searchQuery, filterOption]);


  const handleAddProduct = (type) => {
    if (!canCreateProduct) {
      toast.error("ليس لديك صلاحية لإضافة منتجات جديدة 🔒");
      return;
    }
    console.log(`Product type selected: ${type}`);
    setShowAddProductPopup(false);
  };

  const handleProductClick = (product) => {
    if (activeId) return; // Prevent click during drag

    if (activeIndex === 3) {
      setSelectedStockProduct(product);
      setShowStockOptions(true);
    } else {
      navigate(`/product/${product.id}`);
    }
  };

  const handleStockOptionSelect = (option) => {
    if (!selectedStockProduct) return;

    if (option === "details") {
      navigate(`/product/${selectedStockProduct.id}`);
    } else if (option === "manage") {
      setShowVoucherManagement(true);
    }
    setShowStockOptions(false);
  };

  const handleProductTypeClick = (index) => {
    if (hasChanges) {
      const confirm = window.confirm(
        "لديك تغييرات غير محفوظة. هل تريد المتابعة؟"
      );
      if (!confirm) return;
    }
    setActiveIndex(index);
    setSelectedStockProduct(null);
    setHasChanges(false);
  };

  const handleStockOptionsClose = () => {
    setShowStockOptions(false);
    setSelectedStockProduct(null);
  };

  const handleVoucherManagementClose = () => {
    setShowVoucherManagement(false);
    setSelectedStockProduct(null);
  };

  // Drag and Drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = filteredProducts.findIndex((p) => p.id === active.id);
    const newIndex = filteredProducts.findIndex((p) => p.id === over.id);

    const newProducts = arrayMove(filteredProducts, oldIndex, newIndex);
    setFilteredProducts(newProducts);
    setHasChanges(true);

    // Optimistic update in Redux
    dispatch(
      localReorderProducts({
        activeId: active.id,
        overId: over.id,
        productType: activeIndex,
      })
    );
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Save reorder
  const handleSaveOrder = async () => {
    const orderedIds = filteredProducts.map((p) => p.id);

    try {
      await dispatch(reorderProducts(orderedIds)).unwrap();
      toast.success("تم حفظ الترتيب بنجاح");
      setHasChanges(false);
      setOriginalOrder(orderedIds);
    } catch (error) {
      toast.error("فشل في حفظ الترتيب");
      console.error("Reorder error:", error);
    }
  };

  // Reset order
  const handleResetOrder = () => {
  // Create a map for quick lookup
  const productsMap = new Map(filteredProducts.map(p => [p.id, p]));
  
  // Restore original order
  const restoredProducts = originalOrder
    .map(id => productsMap.get(id))
    .filter(Boolean);
  
  setFilteredProducts(restoredProducts);
  setHasChanges(false);
};

  const productTypes = [
    { label: "الكل", icon: Grid },
    { label: "كمية", icon: Boxes },
    { label: "باقة", icon: Package },
    { label: "مخزون", icon: Box },
  ];

  const activeProduct = filteredProducts.find((p) => p.id === activeId);

  return (
    <div className="p-6 space-y-8" dir="rtl">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between items-center bg-gray-800/50 backdrop-blur p-4 rounded-xl border border-gray-700/50">
        {/* ✅ زر إضافة منتج - محمي بالصلاحية */}
        {canCreateProduct ? (
          <button
            onClick={() => setShowAddProductPopup(true)}
            className="flex items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg transition-all duration-300 group"
          >
            <PlusCircle className="w-5 h-5 transition-transform group-hover:rotate-90" />
            <span>إضافة منتج</span>
          </button>
        ) : (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700">
            <Lock className="w-5 h-5 text-slate-500" />
            <span className="text-slate-400 text-sm">
              ليس لديك صلاحية إضافة منتجات
            </span>
          </div>
        )}

        {/* Product Type Selector */}
        <div className="flex p-1 bg-gray-700/50 rounded-lg">
          {productTypes.map((item, index) => (
            <button
              key={item.label}
              onClick={() => handleProductTypeClick(index)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300
                ${
                  activeIndex === index
                    ? "bg-yellow-500 text-black shadow-md"
                    : "text-gray-400 hover:bg-gray-600/50 hover:text-white"
                }
              `}
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        {/* Search and Filter */}
        <div className="flex items-center gap-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="البحث عن المنتجات..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full min-w-[250px] bg-gray-700/50 text-white pr-10 pl-4 py-2.5 rounded-lg border border-gray-600/50 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors placeholder-gray-400"
            />
          </div>

          <div className="relative isolate">
            {/* Filter Button */}
            <button
              onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
              className="relative flex items-center gap-2 px-4 py-2.5 bg-gray-700/50 text-gray-300 rounded-lg hover:bg-gray-600/50 hover:text-white transition-colors"
            >
              <Filter className="w-5 h-5" />
              <span>
                {filterOption === "All" && "الكل"}
                {filterOption === "Available" && "متوفر"}
                {filterOption === "Unavailable" && "غير متوفر"}
              </span>
            </button>

            {/* Dropdown Menu */}
            {isFilterMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-[9999]"
                  onClick={() => setIsFilterMenuOpen(false)}
                />

                <div className="absolute right-0 mt-2 w-48 z-[10000] overflow-hidden">
                  <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 divide-y divide-gray-700">
                    {[
                      { value: "All", label: "الكل", icon: Filter },
                      {
                        value: "Available",
                        label: "متوفر",
                        icon: CheckCircle2,
                      },
                      {
                        value: "Unavailable",
                        label: "غير متوفر",
                        icon: XCircle,
                      },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setFilterOption(option.value);
                          setIsFilterMenuOpen(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2.5 text-gray-300 hover:bg-gray-700/80 hover:text-white transition-all"
                      >
                        <option.icon
                          className={`w-4 h-4 ${
                            option.value === "Available"
                              ? "text-green-500"
                              : option.value === "Unavailable"
                              ? "text-red-500"
                              : "text-gray-400"
                          }`}
                        />
                        <span className="font-medium">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Save/Reset Buttons */}
    {hasChanges && (
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 p-3 sm:p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
        <div className="flex items-center justify-center gap-2 text-yellow-400">
          <Save className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="font-medium text-sm sm:text-base">لديك تغييرات غير محفوظة</span>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSaveOrder}
            disabled={reordering}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {reordering ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>جاري الحفظ...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>حفظ الترتيب</span>
              </>
            )}
          </button>
          <button
            onClick={handleResetOrder}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all text-sm sm:text-base"
          >
            <RotateCcw className="w-4 h-4" />
            <span>إعادة تعيين</span>
          </button>
        </div>
      </div>
    )}

      {/* Products Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
          <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
          <span className="text-gray-400">جاري تحميل المنتجات...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-red-500">
          <p>{error}</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={filteredProducts.map((p) => p.id)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-4">
              {filteredProducts.map((product) => (
                <DraggableProductCard
                  key={product.id}
                  product={product}
                  onClick={() => handleProductClick(product)}
                  isDragging={activeId === product.id}
                  canUpdate={canUpdateProduct}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeId && activeProduct ? (
              <div className="cursor-grabbing opacity-90 scale-110">
                <div
                  className={`group relative p-3 rounded-xl border shadow-2xl
                    ${
                      activeProduct.status === "available"
                        ? "bg-green-500/20 border-green-500/40"
                        : "bg-red-500/20 border-red-500/40"
                    }
                  `}
                >
                  <div className="relative aspect-square mb-2">
                    {activeProduct.image ? (
                      <img
                        src={activeProduct.image}
                        alt={activeProduct.alt_text || "المنتج"}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-lg">
                        <ShoppingBag className="w-8 h-8 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <h3 className="text-white text-sm font-medium truncate text-center">
                    {activeProduct.name}
                  </h3>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Popups */}
      {canCreateProduct && (
        <AddProductPopup
          showPopup={showAddProductPopup}
          setShowPopup={setShowAddProductPopup}
          onSelect={handleAddProduct}
        />
      )}

      {showStockOptions && (
        <StockOptionsModal
          isOpen={showStockOptions}
          onClose={handleStockOptionsClose}
          onSelect={handleStockOptionSelect}
          productName={selectedStockProduct?.name}
        />
      )}
      {showVoucherManagement && selectedStockProduct && (
        <VoucherManagementModal
          isOpen={showVoucherManagement}
          onClose={handleVoucherManagementClose}
          serviceId={selectedStockProduct.id}
          productName={selectedStockProduct.name}
        />
      )}
    </div>
  );
};

export default Product;